import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Clock, Database, Flag, Globe, Network, RefreshCw, Unplug, Wifi, Zap } from "lucide-react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { smartoltService, type SmartOLTReports } from "../../../services/smartolt.service.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import {
  REPORTS_TABS,
  configuredHref,
  formatDistance,
  formatSignal,
  formatServiceStatus,
  outageCauseTone,
  outageIconTone,
  scatterColor,
  serviceBadgeClass,
  type ReportsTab,
} from "./smartoltReportsUtils.ts"

const empty: SmartOLTReports = {
  selected_olt_id: "all",
  olts: [],
  services: [],
  summary: {
    total: 0,
    online: 0,
    offline: 0,
    pwr: 0,
    los: 0,
    other: 0,
    good: 0,
    warning: 0,
    bad: 0,
    autofind: { epon: 0, gpon: 0, total: 0 },
  },
  recurring_outages: [],
  scatter_points: [],
  pon_averages: [],
  alarm_levels: { critical: 0, major: 0, minor: 0, total: 0 },
  command_levels: { critical: 0, major: 0, minor: 0, total: 0 },
  updated_at: null,
}

const serviceIcons = {
  web: Globe,
  db: Database,
  vpn: Network,
  tr069: Wifi,
  snmp: Flag,
} as const

function SemiGauge({
  title,
  segments,
}: {
  title: string
  segments: { label: string; value: number; color: string; href: string }[]
}) {
  const total = segments.reduce((sum, item) => sum + item.value, 0)
  const radius = 36
  const stroke = 8
  const circ = Math.PI * radius
  let offset = 0
  return (
    <div className="min-w-0 text-center rounded-2xl border border-slate-200/80 bg-white p-3 shadow-xs">
      <svg viewBox="0 0 100 58" className="mx-auto h-14 w-28">
        <path d="M14 50 A 36 36 0 0 1 86 50" fill="none" stroke="#E2E8F0" strokeWidth={stroke} strokeLinecap="round" />
        {segments.map((item) => {
          const len = total > 0 ? (item.value / total) * circ : 0
          const node = (
            <path
              key={item.label}
              d="M14 50 A 36 36 0 0 1 86 50"
              fill="none"
              stroke={item.color}
              strokeWidth={stroke}
              strokeLinecap="butt"
              strokeDasharray={`${len} ${circ}`}
              strokeDashoffset={-offset}
            />
          )
          offset += len
          return node
        })}
      </svg>
      <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-slate-700">{title}</p>
      <p className="mt-1 flex flex-wrap justify-center gap-x-2 text-[11px] text-slate-500">
        {segments.map((item) => (
          <Link key={item.label} to={item.href} className="transition-opacity hover:opacity-80">
            <span style={{ color: item.color }} className="font-semibold">{item.label}:</span>{" "}
            <span className="font-bold text-slate-800">{item.value}</span>
          </Link>
        ))}
      </p>
    </div>
  )
}

export function SmartOLTReportsPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [data, setData] = useState<SmartOLTReports>(empty)
  const [selectedOltId, setSelectedOltId] = useState("all")
  const [tab, setTab] = useState<ReportsTab>("signal")
  const [showAllOutages, setShowAllOutages] = useState(false)

  const load = useCallback(async (oltId: string, silent = false) => {
    if (!silent) setLoading(true)
    setRefreshing(true)
    try {
      const next = await smartoltService.getReports(oltId)
      setData({
        selected_olt_id: next?.selected_olt_id || oltId,
        olts: Array.isArray(next?.olts) ? next.olts : [],
        services: Array.isArray(next?.services) ? next.services : [],
        summary: next?.summary || empty.summary,
        recurring_outages: Array.isArray(next?.recurring_outages) ? next.recurring_outages : [],
        scatter_points: Array.isArray(next?.scatter_points) ? next.scatter_points : [],
        pon_averages: Array.isArray(next?.pon_averages) ? next.pon_averages : [],
        alarm_levels: next?.alarm_levels || empty.alarm_levels,
        command_levels: next?.command_levels || empty.command_levels,
        updated_at: next?.updated_at || null,
      })
    } catch {
      toast.error("Failed to load reports")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    load(selectedOltId)
  }, [load, selectedOltId])

  const oltQuery = selectedOltId === "all" ? "" : selectedOltId
  const summary = data.summary
  const averageSignal = useMemo(() => {
    if (data.scatter_points.length === 0) return null
    return data.scatter_points.reduce((sum, row) => sum + row.rx, 0) / data.scatter_points.length
  }, [data.scatter_points])

  const visibleOutages = showAllOutages ? data.recurring_outages : data.recurring_outages.slice(0, 12)
  const visibleOlts =
    selectedOltId === "all" ? data.olts : data.olts.filter((row) => row.olt_id === selectedOltId)

  const networkNow = [{ name: "Now", Pwr: summary.pwr, LOS: summary.los, other: summary.other, Online: summary.online }]

  return (
    <div className="space-y-4">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Reports & Analytics</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Network optical signals, recurring outage telemetry, and status distributions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => load(selectedOltId, true)}
            disabled={refreshing}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition-all hover:bg-slate-50 hover:text-blue-600 disabled:opacity-50 shadow-xs"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        {/* Left Sidebar */}
        <aside className="space-y-3">
          {/* OLT Filter */}
          <div className="rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-xs">
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">
              Filter by OLT
            </label>
            <select
              value={selectedOltId}
              onChange={(e) => setSelectedOltId(e.target.value)}
              className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 font-medium shadow-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="all">All OLTs ({data.olts.length})</option>
              {data.olts.map((olt) => (
                <option key={olt.olt_id} value={olt.olt_id}>
                  {olt.name}
                </option>
              ))}
            </select>
          </div>

          {/* OLT Status List */}
          <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
            <div className="border-b border-slate-100 bg-slate-50/70 px-3.5 py-2.5">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">OLT Systems</span>
            </div>
            <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {visibleOlts.map((olt) => (
                <Link
                  key={olt.olt_id}
                  to={SMARTOLT_PATHS.oltDetail(olt.olt_id)}
                  className="flex items-center justify-between px-3.5 py-2.5 transition-colors hover:bg-slate-50"
                >
                  <span className="flex min-w-0 items-center gap-2 text-xs font-medium text-slate-800">
                    <Clock className="h-3.5 w-3.5 flex-shrink-0 text-blue-600" />
                    <span className="truncate">{olt.name}</span>
                  </span>
                  <span className="ml-2 flex-shrink-0 font-mono text-[11px] text-slate-500">
                    {olt.uptime_label}
                  </span>
                </Link>
              ))}
              {!loading && data.olts.length === 0 && (
                <p className="px-3 py-6 text-center text-xs text-slate-500">No OLTs synced.</p>
              )}
            </div>
          </div>

          {/* Core Services Status */}
          <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
            <div className="border-b border-slate-100 bg-slate-50/70 px-3.5 py-2.5">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Platform Services</span>
            </div>
            <div className="divide-y divide-slate-100">
              {data.services.map((svc) => {
                const Icon = serviceIcons[svc.id as keyof typeof serviceIcons] || Globe
                return (
                  <div key={svc.id} className="flex items-center justify-between px-3.5 py-2.5">
                    <span className="flex items-center gap-2 text-xs font-medium text-slate-800">
                      <Icon className="h-3.5 w-3.5 text-blue-600" />
                      {svc.label}
                    </span>
                    <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${serviceBadgeClass(svc.status)}`}>
                      {formatServiceStatus(svc.status)}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <section className="space-y-4 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-xs">
          {/* Tabs */}
          <div className="flex flex-wrap gap-1.5 border-b border-slate-100 pb-3">
            {REPORTS_TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                className={`h-8 rounded-lg px-3 text-xs font-semibold transition-all ${
                  tab === item.id
                    ? "bg-blue-50 text-blue-700 border border-blue-200 shadow-xs"
                    : "bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-slate-200 shadow-xs"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Gauges Overview for signal & status tabs */}
          {(tab === "signal" || tab === "status") && (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <SemiGauge
                title="Autofind"
                segments={[
                  { value: summary.autofind.epon, color: "#64748B", label: "EPON", href: SMARTOLT_PATHS.unconfigured },
                  { value: summary.autofind.gpon, color: "#0284C7", label: "GPON", href: SMARTOLT_PATHS.unconfigured },
                ]}
              />
              <SemiGauge
                title="Online State"
                segments={[
                  { value: summary.total, color: "#475569", label: "Total", href: configuredHref({ olt: oltQuery }) },
                  { value: summary.online, color: "#059669", label: "Online", href: configuredHref({ olt: oltQuery, status: "online" }) },
                  { value: summary.offline, color: "#E11D48", label: "Offline", href: configuredHref({ olt: oltQuery, status: "offline" }) },
                ]}
              />
              <SemiGauge
                title="Offline Root"
                segments={[
                  { value: summary.pwr, color: "#D97706", label: "Pwr", href: configuredHref({ olt: oltQuery, status: "power" }) },
                  { value: summary.los, color: "#DC2626", label: "LoS", href: configuredHref({ olt: oltQuery, status: "los" }) },
                  { value: summary.other, color: "#64748B", label: "Other", href: configuredHref({ olt: oltQuery, status: "offline" }) },
                ]}
              />
              <SemiGauge
                title="Optical Quality"
                segments={[
                  { value: summary.good, color: "#059669", label: "Good", href: configuredHref({ olt: oltQuery, signal: "strong" }) },
                  { value: summary.warning, color: "#CA8A04", label: "Warn", href: configuredHref({ olt: oltQuery, signal: "medium" }) },
                  { value: summary.bad, color: "#DC2626", label: "Bad", href: configuredHref({ olt: oltQuery, signal: "weak" }) },
                ]}
              />
            </div>
          )}

          {/* Tab 1: Signal strength scatter plot */}
          {tab === "signal" && (
            <div className="space-y-2 pt-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-bold text-slate-900">ONU Optical Signal vs Fiber Distance</h2>
                <p className="font-mono text-xs text-slate-500">
                  Avg Signal: <strong className="text-blue-600 font-bold">{averageSignal == null ? "—" : `${averageSignal.toFixed(2)} dBm`}</strong>
                </p>
              </div>
              <div className="h-72 w-full rounded-xl border border-slate-200 bg-slate-50/50 p-2">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 12, right: 16, left: -10, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" dataKey="km" name="Distance" unit="km" tick={{ fill: "#64748B", fontSize: 11 }} />
                    <YAxis type="number" dataKey="rx" name="Signal" unit="dBm" domain={[-35, 0]} tick={{ fill: "#64748B", fontSize: 11 }} />
                    <Tooltip
                      cursor={{ strokeDasharray: "3 3", stroke: "#94A3B8" }}
                      contentStyle={{ backgroundColor: "#FFFFFF", borderColor: "#CBD5E1", color: "#0F172A", borderRadius: "0.5rem", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                    />
                    {averageSignal != null && <ReferenceLine y={averageSignal} stroke="#2563EB" strokeDasharray="4 4" />}
                    <Scatter
                      data={data.scatter_points}
                      onClick={(point: { onu_external_id?: string }) => {
                        if (point?.onu_external_id) navigate(SMARTOLT_PATHS.onuDetail(point.onu_external_id))
                      }}
                    >
                      {data.scatter_points.map((point) => (
                        <Cell key={point.onu_external_id} fill={scatterColor(point)} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
              {!loading && data.scatter_points.length === 0 && (
                <p className="py-6 text-center text-xs text-slate-500">No cached distance/signal pairs available yet.</p>
              )}
            </div>
          )}

          {/* Tab 2: Network Status Bar Chart */}
          {tab === "status" && (
            <div className="space-y-2 pt-2">
              <h2 className="text-sm font-bold text-slate-900">Current Network Online / Offline Breakdown</h2>
              <div className="h-64 w-full rounded-xl border border-slate-200 bg-slate-50/50 p-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={networkNow} margin={{ top: 12, right: 16, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fill: "#64748B", fontSize: 11 }} />
                    <Tooltip contentStyle={{ backgroundColor: "#FFFFFF", borderColor: "#CBD5E1", color: "#0F172A", borderRadius: "0.5rem", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }} />
                    <Bar dataKey="Online" stackId="net" fill="#10B981" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Pwr" stackId="net" fill="#F59E0B" />
                    <Bar dataKey="LOS" stackId="net" fill="#EF4444" />
                    <Bar dataKey="other" stackId="net" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Recurring Outages (visible on signal, status, or outages tabs) */}
          {(tab === "signal" || tab === "status" || tab === "outages") && (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-3">
                <div>
                  <h2 className="text-sm font-bold text-slate-900">Device Signal Recurring Outages (24 Hours)</h2>
                  <p className="text-xs text-slate-500">
                    ONUs with frequent disconnects or intermittent fiber instability
                  </p>
                </div>
                {data.recurring_outages.length > 12 && (
                  <button
                    type="button"
                    onClick={() => setShowAllOutages((open) => !open)}
                    className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors"
                  >
                    {showAllOutages ? "Show Top 12" : `View All (${data.recurring_outages.length})`}
                  </button>
                )}
              </div>
              <ul className="divide-y divide-slate-100">
                {visibleOutages.map((row) => {
                  const Icon = row.status_kind === "power" ? Zap : Unplug
                  return (
                    <li key={row.onu_external_id} className="flex items-start gap-3 px-4 py-3 text-xs transition-colors hover:bg-slate-50">
                      <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100 border border-slate-200">
                        <Icon className={`h-3.5 w-3.5 ${outageIconTone(row)}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-800 leading-relaxed">
                          <Link to={SMARTOLT_PATHS.onuDetail(row.onu_external_id)} className="font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                            {row.onu_name}
                          </Link>
                          {" on "}
                          {row.olt_id ? (
                            <Link to={SMARTOLT_PATHS.oltDetail(row.olt_id)} className="text-blue-600 hover:text-blue-800 hover:underline">
                              {row.olt_name || row.olt_id}
                            </Link>
                          ) : "—"}
                          {" port "}
                          <span className="font-mono font-medium text-slate-700">{row.port || "—"}</span>
                          {" disconnected "}
                          <strong className="text-amber-700 font-bold">{row.disconnect_count}×</strong>
                          {" due to "}
                          <span className={outageCauseTone(row)}>{row.cause}</span>
                          {"."}
                        </p>
                        <p className="mt-0.5 text-[11px] text-slate-500 flex flex-wrap gap-x-3">
                          <span>Status: <span className="capitalize font-medium text-slate-700">{row.status_kind}</span></span>
                          <span>Signal: <span className="font-mono text-slate-700">{formatSignal(row.signal_rx)}{row.signal_tx != null ? ` / ${formatSignal(row.signal_tx)}` : ""}</span></span>
                          <span>Distance: <span className="font-mono text-slate-700">{formatDistance(row.distance_m)}</span></span>
                        </p>
                      </div>
                    </li>
                  )
                })}
              </ul>
              {!loading && data.recurring_outages.length === 0 && (
                <p className="px-4 py-8 text-center text-xs text-slate-500">No recurring outages detected in the current scope.</p>
              )}
            </div>
          )}

          {/* Tab 3: Faults summary cards */}
          {tab === "faults" && (
            <div className="space-y-3">
              <h2 className="text-sm font-bold text-slate-900">Fault Breakdown</h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Link
                  to={configuredHref({ olt: oltQuery, status: "los" })}
                  className="rounded-2xl border border-rose-200 bg-rose-50/70 p-4 transition-all hover:bg-rose-50 shadow-xs"
                >
                  <p className="text-3xl font-bold text-rose-700">{summary.los}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wider text-rose-900">Fiber Cut / LoS</p>
                  <p className="mt-0.5 text-[11px] text-rose-700">Loss of optical signal on subscriber line</p>
                </Link>
                <Link
                  to={configuredHref({ olt: oltQuery, status: "power" })}
                  className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 transition-all hover:bg-amber-50 shadow-xs"
                >
                  <p className="text-3xl font-bold text-amber-700">{summary.pwr}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wider text-amber-900">Dying Gasp / Power</p>
                  <p className="mt-0.5 text-[11px] text-amber-700">Power failure alarms emitted by ONT</p>
                </Link>
                <Link
                  to={configuredHref({ olt: oltQuery, status: "offline" })}
                  className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 transition-all hover:bg-slate-50 shadow-xs"
                >
                  <p className="text-3xl font-bold text-slate-700">{summary.other}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wider text-slate-900">Other Offline</p>
                  <p className="mt-0.5 text-[11px] text-slate-500">Disconnected without explicit alarms</p>
                </Link>
              </div>
            </div>
          )}

          {/* Tab 4: Alarms (24h) */}
          {tab === "alarms" && (
            <div className="space-y-3">
              <h2 className="text-sm font-bold text-slate-900">Active Alarm Levels (Past 24 Hours)</h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Link
                  to={SMARTOLT_PATHS.alarms}
                  className="rounded-2xl border border-rose-200 bg-rose-50/70 p-4 transition-all hover:bg-rose-50 shadow-xs"
                >
                  <p className="text-3xl font-bold text-rose-700">{data.alarm_levels.critical}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wider text-rose-900">Critical Alarms</p>
                  <p className="mt-0.5 text-[11px] text-rose-700">Requires immediate engineering attention</p>
                </Link>
                <Link
                  to={SMARTOLT_PATHS.alarms}
                  className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 transition-all hover:bg-amber-50 shadow-xs"
                >
                  <p className="text-3xl font-bold text-amber-700">{data.alarm_levels.major}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wider text-amber-900">Major Alarms</p>
                  <p className="mt-0.5 text-[11px] text-amber-700">Service degradations and intermittent link drops</p>
                </Link>
                <Link
                  to={SMARTOLT_PATHS.diagnostics}
                  className="rounded-2xl border border-blue-200 bg-blue-50/70 p-4 transition-all hover:bg-blue-50 shadow-xs"
                >
                  <p className="text-3xl font-bold text-blue-700">{data.alarm_levels.minor}</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wider text-blue-900">Minor / Informational</p>
                  <p className="mt-0.5 text-[11px] text-blue-700">System notices, registrations, and low priority logs</p>
                </Link>
              </div>
            </div>
          )}

          {/* Tab 5: PON average signal table */}
          {tab === "pon" && (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs">
              <div className="border-b border-slate-100 bg-slate-50/70 px-4 py-3">
                <h2 className="text-sm font-bold text-slate-900">PON Port Average Optical Rx</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-xs">
                  <thead className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    <tr>
                      <th className="px-4 py-2.5">OLT</th>
                      <th className="px-4 py-2.5">Board</th>
                      <th className="px-4 py-2.5">Port</th>
                      <th className="px-4 py-2.5 text-right">ONUs</th>
                      <th className="px-4 py-2.5 text-right">Avg RX Signal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-800">
                    {data.pon_averages.map((row) => (
                      <tr key={`${row.olt_id}-${row.board}-${row.port}`} className="transition-colors hover:bg-slate-50">
                        <td className="px-4 py-2.5 font-medium text-slate-800">{row.olt_name || row.olt_id}</td>
                        <td className="px-4 py-2.5 font-mono text-slate-500">{row.board || "—"}</td>
                        <td className="px-4 py-2.5 font-mono text-slate-500">{row.port || "—"}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-blue-600">{row.onus}</td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold text-emerald-600">{row.avg_rx} dBm</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!loading && data.pon_averages.length === 0 && (
                <p className="px-4 py-8 text-center text-xs text-slate-500">No PON averages available yet.</p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
